import { useCallback, useState } from 'react';
import axios from 'axios';
import { useSWRConfig } from 'swr';
import { APPROVAL_V2_URL } from '@/constant/approval';
import { deleteDataFetcher } from '@/utils/apis';

export type UseDeleteApprovalOptions = {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

/**
 * 품의 문서 삭제 mutation.
 * - DELETE /api/v2/approval/{documentId}
 * - 삭제 가능 조건: 반려(R) 또는 임시저장(S) + 기안자 본인. 회수도 R 로 전환되므로 회수 후 삭제 가능.
 * - 응답 코드:
 *   204 삭제 성공
 *   403 기안자 본인 아님
 *   404 문서를 찾을 수 없음
 *   422 삭제할 수 없는 상태
 *   status code → 메시지 매핑은 `getDeleteApprovalErrorMessage` 사용.
 */
const useDeleteApproval = (options?: UseDeleteApprovalOptions) => {
  const { mutate } = useSWRConfig();
  const [isDeleting, setIsDeleting] = useState(false);

  const remove = useCallback(
    async (documentId: string): Promise<void> => {
      setIsDeleting(true);
      const key = `${APPROVAL_V2_URL}/${documentId}`;
      try {
        await deleteDataFetcher(key);
        // 단건 캐시 무효화 — 문자열 key(useApproval) + 튜플 key(useApprovalView) 동시 매칭.
        await mutate(
          (cacheKey) =>
            cacheKey === key ||
            (Array.isArray(cacheKey) && cacheKey[0] === key),
          undefined,
          { revalidate: false },
        );
        options?.onSuccess?.();
      } catch (error) {
        options?.onError?.(error);
        throw error;
      } finally {
        setIsDeleting(false);
      }
    },
    [mutate, options],
  );

  return { remove, isDeleting };
};

/**
 * 삭제 실패 시 status code → 사용자 메시지(번역 key) 매핑.
 * 호출부에서 `t(getDeleteApprovalErrorMessage(error))` 로 사용.
 */
export const getDeleteApprovalErrorMessage = (error: unknown): string => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  switch (status) {
    case 403:
      return '기안자 본인만 삭제할 수 있습니다.';
    case 404:
      return '문서를 찾을 수 없습니다.';
    case 422:
      return '삭제할 수 없는 상태의 품의입니다.';
    default:
      return '삭제에 실패했습니다.';
  }
};

export default useDeleteApproval;
